import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminFaq.css";

/* webpack require.context for FAQ images */
let FAQ_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/faq",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  FAQ_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function faqImg(filename) {
  if (!filename) return null;
  return FAQ_IMAGES[filename] ?? `${API_BASE}/uploads/faq/${filename}`;
}

const EMPTY_FORM = {
  title:       "",
  description: "",
  order:       "999",
  active:      1,
  image:       null, // new file upload
};

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="afq-dot-menu" ref={ref}>
      <button
        className="afq-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="afq-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="afq-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="afq-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="afq-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="afq-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`afq-badge ${isActive ? "afq-badge--active" : "afq-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ faq, onClose }) {
  if (!faq) return null;

  return (
    <div className="afq-modal-overlay" onClick={onClose}>
      <div className="afq-modal afq-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="afq-modal__header">
          <div className="afq-modal__title-row">
            <span className="afq-modal__tag">VIEW</span>
            <h2 className="afq-modal__title">{faq.title || "FAQ"}</h2>
          </div>
          <button className="afq-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="afq-modal__body afq-modal__body--view">
          {/* Image */}
          {faq.image_filename ? (
            <img
              src={faqImg(faq.image_filename)}
              alt={faq.title || ""}
              className="afq-view-image"
            />
          ) : (
            <div className="afq-view-image--placeholder">
              <span>❓</span>
            </div>
          )}

          {/* Details grid */}
          <div className="afq-view-grid">
            {[
              ["Title",  faq.title  || "—"],
              ["Order",  faq.order  ?? "—"],
              ["Status", Number(faq.active) === 1 ? "Active" : "Inactive"],
              ["Created", faq.created_at ? new Date(faq.created_at).toLocaleDateString("en-ZA") : "—"],
            ].map(([label, val]) => (
              <div key={label} className="afq-view-row">
                <span className="afq-view-row__label">{label}</span>
                <span className="afq-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Description */}
          {faq.description && (
            <div className="afq-view-description">
              <h4>Answer</h4>
              <p className="afq-view-description__text">{faq.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, faq, onClose, onSaved }) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);
  const [clearImg, setClearImg] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && faq) {
      setForm({
        title:       faq.title       || "",
        description: faq.description || "",
        order:       faq.order       ?? "999",
        active:      Number(faq.active) === 1 ? 1 : 0,
        image:       null,
      });
      setPreview(faq.image_filename ? faqImg(faq.image_filename) : null);
      setClearImg(false);
    }
  }, [mode, faq]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFile = (file) => {
    if (!file) return;
    set("image", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    set("image", null);
    setPreview(null);
    setClearImg(true);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) return setError("FAQ question/title is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",       form.title.trim());
      fd.append("description", form.description || "");
      fd.append("order",       parseInt(form.order) || 999);
      fd.append("active",      form.active);
      if (form.image)  fd.append("image_filename", form.image);
      if (clearImg)    fd.append("clear_image", "1");

      if (mode === "create") {
        await apiFetch("/api/admin/faq", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/faq/${faq.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="afq-modal-overlay" onClick={onClose}>
      <div className="afq-modal afq-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="afq-modal__header">
          <div className="afq-modal__title-row">
            <span className="afq-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="afq-modal__title">
              {mode === "create" ? "Add FAQ" : "Edit FAQ"}
            </h2>
          </div>
          <button className="afq-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="afq-modal__body afq-modal__body--form">
          {error && <div className="afq-modal__error">⚠ {error}</div>}

          {/* ── Section: FAQ Details ── */}
          <div className="afq-form-section">
            <h3 className="afq-form-section__title">FAQ Details</h3>
            <div className="afq-form-grid">
              <div className="afq-form-field afq-form-field--full">
                <label>Question / Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. How long does a service take?"
                />
              </div>

              <div className="afq-form-field afq-form-field--full">
                <label>Answer</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="e.g. Most standard services are completed within 2–4 hours depending on the type of work required."
                />
              </div>
            </div>
          </div>

          {/* ── Section: Image ── */}
          <div className="afq-form-section">
            <h3 className="afq-form-section__title">Image <span className="afq-form-section__optional">(optional)</span></h3>
            <label className="afq-image-upload">
              {preview ? (
                <img src={preview} alt="" className="afq-image-upload__preview" />
              ) : (
                <div className="afq-image-upload__placeholder">
                  <span>🖼</span>
                  <span>Click to upload image</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <span className="afq-image-upload__label">
                {preview ? "Click to change image" : "Upload Image"}
              </span>
            </label>
            {preview && (
              <button
                type="button"
                className="afq-image-clear"
                style={{ marginTop: 6 }}
                onClick={handleClear}
              >
                Remove image
              </button>
            )}
            <p className="afq-form-hint">
              Images are saved to <code>src/assets/uploads/faq/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Display Options ── */}
          <div className="afq-form-section">
            <h3 className="afq-form-section__title">Display Options</h3>
            <div className="afq-form-grid">
              <div className="afq-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="999"
                />
                <p className="afq-form-hint">Lower numbers appear first. Default is 999.</p>
              </div>

              <div className="afq-form-field afq-form-field--check-col">
                <label className="afq-form-label--block">Visibility</label>
                <label className="afq-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="afq-modal__footer">
          <button className="afq-btn afq-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="afq-btn afq-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create FAQ" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ faq, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/faq/${faq.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="afq-modal-overlay" onClick={onClose}>
      <div className="afq-modal afq-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="afq-delete__icon">🗑</div>
        <h2 className="afq-delete__title">Delete FAQ?</h2>
        <p className="afq-delete__body">
          You are about to permanently delete{" "}
          <strong>{faq?.title || "this FAQ item"}</strong>. This will also remove the
          associated image file. This action cannot be undone.
        </p>
        {error && <div className="afq-modal__error">⚠ {error}</div>}
        <div className="afq-delete__actions">
          <button className="afq-btn afq-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="afq-btn afq-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminFaq() {
  const [faqs,       setFaqs]       = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort,         setSort]         = useState("order_asc");
  const [page,         setPage]         = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load FAQs ── */
  const loadFaqs = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/faq?${q}`)
      .then((data) => {
        setFaqs(data.faqs       || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, sort, page]);

  useEffect(() => { loadFaqs(); }, [loadFaqs]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, item = null) => { setModal(type); setTarget(item); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadFaqs(); };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="FAQ">

      {error && (
        <div className="afq-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="afq-toolbar">
        <div className="afq-toolbar__left">
          <div className="afq-search-wrap">
            <span className="afq-search-wrap__icon">🔍</span>
            <input
              className="afq-search"
              type="text"
              placeholder="Search FAQ by title or answer…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="afq-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="afq-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="afq-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="afq-toolbar__right">
          <span className="afq-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} item${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="afq-btn-primary" onClick={() => openModal("create")}>
            + Add FAQ
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="afq-table-skeleton" />
      ) : faqs.length === 0 ? (
        <div className="afq-empty">
          <span>❓</span>
          <p>No FAQ items found.</p>
          {(search || statusFilter) && (
            <button
              className="afq-btn afq-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="afq-table-wrap">
          <table className="afq-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Question</th>
                <th>Answer</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.image_filename ? (
                      <img
                        src={faqImg(item.image_filename)}
                        alt=""
                        className="afq-img-thumb"
                      />
                    ) : (
                      <span className="afq-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <span className="afq-title-cell">{item.title || "—"}</span>
                  </td>
                  <td>
                    <span className="afq-desc-preview">
                      {item.description
                        ? item.description.slice(0, 120) + (item.description.length > 120 ? "…" : "")
                        : "—"}
                    </span>
                  </td>
                  <td>
                    <span className="afq-order-badge">{item.order ?? 999}</span>
                  </td>
                  <td>
                    <StatusBadge active={item.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()   => openModal("view",   item)}
                      onEdit={()   => openModal("edit",   item)}
                      onDelete={() => openModal("delete", item)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="afq-pagination">
          <button
            className="afq-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`afq-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="afq-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   faq={target}    onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"   onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"     faq={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal faq={target}    onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}